import { Update, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import { Effect, pipe } from 'effect'
import { isMatching, match, P } from '@st/pattern'
import { SdkClient } from '~/effects'
import { observeRoute, runSdkRequest } from '~/features/module-runtime'
import { routes } from '~/features/routes'

type Invitation = {
  organizationSlug: string
  email: string
  origin: string
}

export type OrganizationTeamInviteState =
  | { status: 'idle' }
  | (Invitation & { status: 'ready' | 'sending' | 'sendFailed' | 'sent' })
  | (Invitation & { status: 'finishing'; nextInvitation: Invitation | null })

const observeTeamInviteRoute = observeRoute({ name: 'team_invitation' })

export type OrganizationTeamInviteMessage =
  | TaskMessage<typeof observeTeamInviteRoute>
  | { type: 'sendPasswordResetEmail' }
  | { type: 'passwordResetEmailSettled'; result: 'sent' | 'failed' }

/**
 * The global organization context already owns organization resolution and
 * branding. This chart owns only the invitation URL and its one write:
 *
 * idle --matching route--> ready --send--> sending --success--> sent
 *                                      |
 *                              transient failure
 *                                      v
 *                                 sendFailed --retry--> sending
 *
 * The route observation is a Task. Sending the email is an accepted Command,
 * so leaving the route moves the chart to `finishing` rather than canceling it.
 */
export const organizationTeamInviteModule = defModule<{
  state: OrganizationTeamInviteState
  message: OrganizationTeamInviteMessage
}>({
  init: () => ({ status: 'idle' }),
  tasks: () => [observeTeamInviteRoute()],
  update: pipe(
    updateOrganizationTeamInvite,
    Update.ignoreWhen<OrganizationTeamInviteState, OrganizationTeamInviteMessage>(
      isMatching({
        state: { status: 'sending' },
        message: { type: 'sendPasswordResetEmail' }
      })
    )
  )
})

function updateOrganizationTeamInvite(
  state: OrganizationTeamInviteState,
  message: OrganizationTeamInviteMessage
): UpdateResult<OrganizationTeamInviteState, OrganizationTeamInviteMessage> {
  return match({ state, message })
    .returnType<UpdateResult<OrganizationTeamInviteState, OrganizationTeamInviteMessage>>()
    .with({ message: { type: 'routeChanged' } }, ({ state, message }) => {
      const invitation = invitationFromRoute(message.route, message.location.origin)
      if (state.status == 'sending') {
        if (invitation && sameInvitation(state, invitation)) return state
        return { ...state, status: 'finishing', nextInvitation: invitation }
      }
      if (state.status == 'finishing') {
        if (invitation && sameInvitation(state, invitation)) {
          const { nextInvitation: _, ...sending } = state
          return { ...sending, status: 'sending' }
        }
        return { ...state, nextInvitation: invitation }
      }
      if (!invitation) return { status: 'idle' }
      if (state.status != 'idle' && sameInvitation(state, invitation)) {
        return state
      }
      return { status: 'ready', ...invitation }
    })
    .with(
      {
        state: { status: P.union('ready', 'sendFailed') },
        message: { type: 'sendPasswordResetEmail' }
      },
      ({ state }) => [{ ...state, status: 'sending' }, sendPasswordResetEmail(state)]
    )
    .with(
      {
        state: { status: 'sending' },
        message: { type: 'passwordResetEmailSettled', result: 'sent' }
      },
      ({ state }) => ({ ...state, status: 'sent' })
    )
    .with(
      {
        state: { status: 'sending' },
        message: { type: 'passwordResetEmailSettled', result: 'failed' }
      },
      ({ state }) => ({ ...state, status: 'sendFailed' })
    )
    .with(
      { state: { status: 'finishing' }, message: { type: 'passwordResetEmailSettled' } },
      ({ state }) => {
        if (state.nextInvitation) return { status: 'ready', ...state.nextInvitation }
        return { status: 'idle' }
      }
    )
    .with(
      {
        state: { status: P.union('idle', 'sent') },
        message: { type: 'sendPasswordResetEmail' }
      },
      ({ state, message }) => unexpectedMessage(state, message)
    )
    .otherwise(({ state, message }) => unexpectedMessage(state, message))
}

function invitationFromRoute(
  route: TaskMessage<typeof observeTeamInviteRoute>['route'],
  origin: string
): Invitation | null {
  if (!route) return null
  return {
    organizationSlug: route.organizationSlug,
    email: route.email,
    origin
  }
}

function sendPasswordResetEmail(invitation: Invitation) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* runSdkRequest(
      sdk.send({
        type: 'accounts/sendPasswordResetEmail',
        email: invitation.email,
        continueUrl: continuationUrl(invitation.origin, invitation.organizationSlug)
      })
    )
    if (!result.ok) return { type: 'passwordResetEmailSettled', result: 'failed' } as const
    return { type: 'passwordResetEmailSettled', result: 'sent' } as const
  })
}

function sameInvitation(left: Invitation, right: Invitation): boolean {
  return (
    left.organizationSlug == right.organizationSlug &&
    left.email == right.email &&
    left.origin == right.origin
  )
}

function continuationUrl(origin: string, organizationSlug: string): string {
  return (
    origin +
    routes.toPath({
      name: 'folder_list',
      organizationSlug
    })
  )
}

function unexpectedMessage(
  state: OrganizationTeamInviteState,
  message: OrganizationTeamInviteMessage
): never {
  throw new Error(
    `Unexpected organization team invite message ${message.type} while ${state.status}`
  )
}
